import classNames from 'classnames';
import styles from './M28ProductOverview.module.scss';
import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { Container } from 'ui/components/4-habitats/Container';
import { Slider } from 'ui/components/3-organisms/Slider';
import { Heading, RichText, LinkButton } from 'ui/components/1-atoms';
import { IconList } from 'ui/components/3-organisms/IconList';
import { SliderItemProps } from 'ui/components/3-organisms/Slider/SliderItem/SliderItem';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';

export interface M28ProductOverviewProps {
	className?: string;
	dictionary?: { [key: string]: string };
	anchor?: string;
	carouselAriaControls?: string;
	carouselImages?: SliderItemProps[];
	heading?: string;
	subheading?: string;
	factHeading?: string;
	facts?: string[];
	description?: string;
	contactFormLink?: string;
	onContactClick?: React.MouseEventHandler<HTMLAnchorElement>;
	contactFormLoading?: boolean;
}

export const M28ProductOverview: React.FC<M28ProductOverviewProps> = ({
	className,
	dictionary,
	anchor,
	heading,
	carouselAriaControls,
	carouselImages,
	subheading,
	factHeading,
	facts,
	description,
	contactFormLink,
	onContactClick,
	contactFormLoading,
}) => {
	const textBoxRef = useRef<HTMLDivElement>(null);
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const richTextEl = useRef<HTMLDivElement>(null);
	const sliderRef = useRef<HTMLDivElement>(null);

	const [showDetails, setShowDetails] = useState(false);
	const [overflowingText, setOverflowingText] = useState(false);

	const toggleDescription = (e) => {
		setShowDetails((e.target as HTMLDetailsElement).open);
	};

	useEffect(() => {
		const measure = () => {
			const sliderH = sliderRef.current?.getBoundingClientRect().height ?? 0;
			const boxH = textBoxRef.current?.getBoundingClientRect().height ?? 0;
			const contentH = richTextEl.current?.scrollHeight ?? 0;
			// Rule:
			// - show "Show more" if the text overflows its own box (classic)
			// - OR if the content is taller than the slider (keeps columns visually balanced)
			const overflowsSelf = contentH > textBoxRef.current?.clientHeight + 1;
			const exceedsSlider = sliderH > 0 && boxH > sliderH;
			setOverflowingText(overflowsSelf || exceedsSlider);
		};

		measure(); // after first paint
		const t = setTimeout(measure, 300); // after Swiper’s settle
		return () => clearTimeout(t);
	}, []);

	// Keep <details> open/closed in sync
	useEffect(() => {
		if (!detailsRef.current) return;
		if (!overflowingText) {
			detailsRef.current.setAttribute('open', '');
		} else {
			detailsRef.current.removeAttribute('open');
		}
	}, [textBoxRef, sliderRef, overflowingText]);

	return (
		<div id={anchor} className={classNames(styles.M28ProductOverview, className)}>
			<Container width="StandardWeb" className={styles.M28ProductOverview_wrapper}>
				<div className={styles.M28ProductOverview_colRight}>
					<Slider
						sliderRef={sliderRef}
						slides={carouselImages}
						autoHeight={true}
						className={styles.M28ProductOverview_slider}
						navButtonsClassName={styles.M28ProductOverview_navButtons}
						dictionary={dictionary}
						sliderAriaControls={carouselAriaControls}
						slideAspectRatio={1.5}
					></Slider>
				</div>

				<div className={styles.M28ProductOverview_colLeft} ref={textBoxRef}>
					<div className={styles.M28ProductOverview_headingsContainer}>
						<Heading headingLevel="h2" style="displaySm" className={styles.M28ProductOverview_heading}>
							{heading}
						</Heading>
						<p className={styles.M28ProductOverview_subHeading}>{subheading}</p>
					</div>
					<Heading headingLevel="h3" className={styles.M28ProductOverview_factHeading}>
						{factHeading}
					</Heading>
					<IconList itemList={facts} className={classNames(styles.M28ProductOverview_iconList)} />

					<div className={styles.M28ProductOverview_descriptionContainer}>
						<details
							ref={detailsRef}
							className={classNames(styles.M28ProductOverview_details, {
								[styles.M28ProductOverview_details___notOverflowed]: !overflowingText,
							})}
							onToggle={(e) => toggleDescription(e)}
						>
							<summary aria-expanded={showDetails} className={styles.M28ProductOverview_summary}>
								{overflowingText && (
									<>
										<span className={styles.M28ProductOverview_summaryText}>
											{showDetails ? dictionary?.showLess : dictionary?.showMore}
										</span>
										<i className={styles.M28ProductOverview_summaryIcon} aria-hidden="true"></i>
									</>
								)}
							</summary>
						</details>
						<RichText variant="website" content={description} className={classNames(styles.M28ProductOverview_description)} />
						{contactFormLink && (
							<LinkButton
								style="primaryWeb"
								className={classNames(styles.M28ProductOverview_button)}
								title={dictionary?.buttonTitle}
								ariaLabel={dictionary?.buttonTitle}
								url={contactFormLink}
								target="_self"
								withBackIcon={<IconArrowRight />}
								iconClassName={styles.M28ProductOverview_buttonIcon}
								handleClick={onContactClick}
								disabled={contactFormLoading}
								showSpinner={contactFormLoading}
							>
								{dictionary?.buttonTitle}
							</LinkButton>
						)}
					</div>
				</div>
			</Container>
		</div>
	);
};
